// ─── El historial de una animación por código ────────────────────────────────
// Cada vez que el agente crea o cambia la animación de un clip queda una versión
// más, y el usuario puede volver a cualquiera ("volvé a la anterior", o desde el
// panel). Todo puro: el clip guarda este objeto en `animationCodigo` y VideoEditor
// lo escribe con `setClips` como a cualquier otro campo.

import { MAX_VERSIONES } from './reglas';

export type TipoDeAnimacionPorCodigo = 'superpuesta' | 'escena';

export type AssetDeAnimacion = {
  /** URL firmada (se renueva en el export, como la de cualquier imagen). */
  url: string;
  /** Para que el código sepa qué es ("foto del bolso rojo", "logo"). */
  descripcion: string;
  /** Para volver a firmar la URL al exportar. */
  storagePath?: string;
};

export type VersionDeAnimacion = {
  /** El componente (TSX con `export default`). */
  codigo: string;
  /** Qué se pidió o qué cambió, en palabras del agente ("más lento, con el logo"). */
  resumen: string;
  /** ISO. */
  fecha: string;
  /** La duración que el generador propuso para esta versión, en segundos. */
  duracionSeg: number;
};

/** La marca como la recibe el código por props. Se congela al crear la animación. */
export type MarcaDeAnimacion = { colores: string[]; fuentes: string[]; nombre: string };

export type AnimacionPorCodigo = {
  /** Índice de la versión que se ve, dentro de `versiones`. */
  actual: number;
  versiones: VersionDeAnimacion[];
  tipo: TipoDeAnimacionPorCodigo;
  /** Fotos y logos que el código recibe por props (nunca hardcodeadas). */
  assets: AssetDeAnimacion[];
  marca: MarcaDeAnimacion;
  /** Con qué entrada de la biblioteca del proyecto (Archivos → Animaciones) está
   *  enganchada: un cambio del agente sobre el clip actualiza también la guardada. */
  bibliotecaId?: string;
};

export function crearAnimacionPorCodigo(p: {
  version: VersionDeAnimacion;
  tipo: TipoDeAnimacionPorCodigo;
  assets: AssetDeAnimacion[];
  marca: MarcaDeAnimacion;
}): AnimacionPorCodigo {
  return { actual: 0, versiones: [p.version], tipo: p.tipo, assets: p.assets, marca: p.marca };
}

export function versionActual(a: AnimacionPorCodigo): VersionDeAnimacion {
  return a.versiones[Math.min(Math.max(0, a.actual), a.versiones.length - 1)]!;
}

/**
 * Suma una versión y la deja como actual. Con más de `MAX_VERSIONES`, descarta
 * la más vieja que no sea la que estaba activa (para que "volver a la anterior"
 * siga teniendo sentido justo después de un cambio).
 */
export function agregarVersion(a: AnimacionPorCodigo, v: VersionDeAnimacion): AnimacionPorCodigo {
  const versiones = [...a.versiones, v];
  let actualPrevio = a.actual;
  while (versiones.length > MAX_VERSIONES) {
    const idx = versiones.findIndex((_, i) => i !== actualPrevio);
    versiones.splice(idx, 1);
    if (idx < actualPrevio) actualPrevio -= 1;
  }
  return { ...a, versiones, actual: versiones.length - 1 };
}

/** Cambia la versión visible. Un índice fuera de rango deja todo como está. */
export function volverAVersion(a: AnimacionPorCodigo, indice: number): AnimacionPorCodigo {
  if (!Number.isInteger(indice) || indice < 0 || indice >= a.versiones.length) return a;
  return { ...a, actual: indice };
}

/** Las versiones como las lee el agente en `list_timeline_clips`: número 1-based y resumen. */
export function describirVersiones(a: AnimacionPorCodigo): Array<{ version: number; resumen: string; actual: boolean }> {
  return a.versiones.map((v, i) => ({ version: i + 1, resumen: v.resumen, actual: i === a.actual }));
}
